import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { customInstance } from '../services/api-client';
import { WorkspaceCatalogCoverageRow } from '../components/workspace-text-to-sql';

export interface CatalogSyncResponse {
  status: string;
  assets_synced: number;
  terms_synced: number;
  links_resolved: number;
}

export interface CatalogAsset {
  id: number;
  external_id: string;
  provider_type: string;
  asset_type: string;
  name: string;
  display_name?: string;
  description?: string;
  certification_status?: string;
  owners?: string[];
}

export interface CatalogCoverage {
  total_warehouse_tables: number;
  linked_warehouse_tables: number;
  described_warehouse_tables?: number;
  coverage_percentage: number;
}

/**
 * Hook for managing Catalog Registry operations (Sync, Browse, Coverage).
 * Used in Phase 3A of Workspace Text-to-SQL integration.
 */
export const useCatalogSync = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (configId: number) =>
      customInstance<CatalogSyncResponse>({
        url: `/api/v1/catalog/sync/${configId}`,
        method: 'POST',
      }),
    onSuccess: (data) => {
      // Invalidate coverage queries to refresh UI after successful sync
      queryClient.invalidateQueries({ queryKey: ['catalog-coverage'] });
      queryClient.invalidateQueries({ queryKey: ['catalog-assets'] });
    },
  });
};

export const useCatalogAssets = (configId: number | undefined) => {
  return useQuery({
    queryKey: ['catalog-assets', configId],
    queryFn: () =>
      customInstance<CatalogAsset[]>({
        url: `/api/v1/catalog/connectors/${configId}/assets`,
        method: 'GET',
      }),
    enabled: !!configId,
  });
};

export const useCatalogCoverage = (workspaceId: number | undefined, enabled = true) => {
  return useQuery({
    queryKey: ['catalog-coverage', workspaceId],
    queryFn: () =>
      customInstance<CatalogCoverage>({
        url: `/api/v1/catalog/workspaces/${workspaceId}/coverage`,
        method: 'GET',
      }),
    enabled: !!workspaceId && enabled,
  });
};

export const useCatalogCoverageDetails = (workspaceId: number | undefined, enabled = true) => {
  return useQuery({
    queryKey: ['catalog-coverage-details', workspaceId],
    queryFn: () =>
      customInstance<WorkspaceCatalogCoverageRow[]>({
        url: `/api/v1/catalog/workspaces/${workspaceId}/coverage/details`,
        method: 'GET',
      }),
    enabled: !!workspaceId && enabled,
  });
};
